<script setup lang="ts">
import { GROUP_ROLE } from '@/enums/group';
import type { EventDuplicateForm, EventDuplicateFormRequest } from '@/types/event';
import dayjs from 'dayjs';

interface NotificationMessage {
	title: string;
	message: string;
}

const eventStore = useEventStore();
const profileStore = useProfileStore();
const groupStore = useGroupStore();
const userStore = useUserStore();
const route = useRoute();
const router = useRouter();
const { isMobile } = useWindowResize();
const notificationStore = useNotificationStore();
const { trackEvent } = useGtm();
const { setDocumentTitle } = useSetDocumentTitle();
const { retrieveEventSeriesDetail } = usePollingEventSeries();

const showDialog = reactive({
	confirmDuplicate: false,
	confirmDelete: false,
	confirmCancel: false,
});
const viewBtnRef = ref();
const duplicateBtnRef = ref();
const deleteBtnRef = ref();
const cancelBtnRef = ref();
const isCancellingEvent = ref(false);
const isCreatingEventSeries = ref(false);
const isCheckingPassword = ref(false);

const isLoading = computed(
	() =>
		eventStore.isLoading ||
		profileStore.isLoading ||
		groupStore.isLoading ||
		eventStore.protect.isPasswordProtected,
);
const isInformPage = computed(() => route.name === ROUTE.EVENT_ATTENDEES_FILE.NAME);

const eventId = computed(() => {
	const protectedRoutes = [ROUTE.USER.NAME, ROUTE.GROUP.NAME];

	if (!protectedRoutes.includes(String(route.name))) {
		return route.params?.id;
	}
	return null;
});

const groupName = computed(() => groupStore.groupInfo?.name);

const eventCancellationId = computed(() => eventStore.cancel.data?.id || null);

const tabs = computed(() => {
	return [
		{ name: 'Edit event', route: { name: ROUTE.EVENT_EDIT.NAME, params: { id: eventId.value } } },
		{
			name: 'Advanced settings',
			route: { name: ROUTE.EVENT_ADVANCED_SETTINGS.NAME, params: { id: eventId.value } },
		},
		{
			name: 'Attendees',
			route: { name: ROUTE.EVENT_ATTENDEES.NAME, params: { id: eventId.value } },
		},
		{
			name: 'Sales & Analytics',
			route: { name: ROUTE.EVENT_SALE_ANALYTICS.NAME, params: { id: eventId.value } },
		},
		{
			name: 'Questionnaire',
			route: { name: ROUTE.EVENT_QUESTIONNAIRE.NAME, params: { id: eventId.value } },
		},
		{
			name: 'QR check in',
			route: { name: ROUTE.EVENT_CHECK_IN.NAME, params: { id: eventId.value } },
		},
		{
			name: 'Invites',
			route: { name: ROUTE.EVENT_INVITES.NAME, params: { id: eventId.value } },
		},
	];
});

const activeTab = ref(tabs.value[0]); // Initialize with the first tab by default

const showTabs = computed(() => {
	return (
		route.name !== ROUTE.EVENT_CREATE.NAME &&
		route.name !== ROUTE.EVENT_PREVIEW.NAME &&
		route.name !== ROUTE.EVENT_VIEW.NAME &&
		route.name !== ROUTE.EVENT.NAME &&
		route.name !== ROUTE.EVENT_ATTENDEES_FILE.NAME
	);
});

const hasEventData = computed(() => {
	return eventStore.eventDetail;
});

const isDraft = computed(() => {
	return eventStore.eventStatus === EVENT_STATUS.DRAFT;
});

const isPublished = computed(() => {
	// Check secondary condition on datetime compared with current time when event status is sold_out
	if (eventStore.eventStatus === EVENT_STATUS.SOLD_OUT) {
		return eventStore.isSoldOutBeforeFinished;
	}
	return eventStore.eventStatus === EVENT_STATUS.OPEN;
});

const isFinished = computed(() => {
	return eventStore.originalEventStatus === EVENT_STATUS.FINISHED;
});

const isCancelled = computed(() => {
	return eventStore.eventStatus === EVENT_STATUS.CANCELED;
});

const isPublicFacing = computed(() => {
	return route.name === ROUTE.EVENT_VIEW.NAME;
});

const alertEventMessage = computed(() => {
	if (route.name === ROUTE.EVENT_CREATE.NAME) {
		return '';
	} else if (isCancelled.value && isPublicFacing.value) {
		return 'This event has been canceled.';
	} else if (isCancelled.value && !isPublicFacing.value) {
		return 'This event has been canceled and cannot be modified.';
	} else if (isFinished.value && isPublicFacing.value) {
		return 'This event has finished.';
	} else if (isFinished.value && !isPublicFacing.value) {
		return 'This event has finished and cannot be modified.';
	}
	return '';
});

const alertCountdownReminderMessage = computed(() => {
	if (eventStore.isEditableAfterEventEnd && !isPublicFacing.value) {
		// Calculate datetime 24 hour from end date
		const endDateAdded24Hour = dayjs(eventStore.edit.data?.end.utc, DATE_TIME_ISO8601_FORMAT, true)
			.add(24, 'hour')
			.format(DATE_TIME_ISO8601_FORMAT);
		// Get tz abbreviation
		const timezoneAbbreviation = getTimezoneAbbreviation(eventStore.edit.data?.end.timezone || '');
		const convertedDateTime = convertDateTimeFromUtcToTimezone(
			endDateAdded24Hour,
			eventStore.edit.data?.end.timezone || '',
			`${SETTING_DISPLAY_DATE_FORMAT} ${TIME_12_HOUR_2_DIGITS_FORMAT}`,
		);
		const message = 'You have until {0} to undo check in and/or cancel order.';
		return replaceTextInCurlyBraces(message, [`${convertedDateTime} ${timezoneAbbreviation}`]);
	}
	return '';
});

async function handleDuplicateEvent(data: EventDuplicateForm) {
	// Set loading state while event series creating
	isCreatingEventSeries.value = true;

	const startDateTime = convertDateTimeToUtc(data.startDateTime, data.timezone);
	const endDateTime = convertDateTimeToUtc(data.endDateTime, data.timezone);
	const payload: EventDuplicateFormRequest = {
		start: {
			utc: startDateTime,
			timezone: data.timezone,
		},
		end: {
			utc: endDateTime,
			timezone: data.timezone,
		},
	};

	const notificationMessage: NotificationMessage = {
		title: '',
		message: '',
	};

	switch (data.eventFrequency) {
		case EVENT_RECURRING_TYPE.ONCE:
			notificationMessage.title = 'Successfully duplicated the event';
			notificationMessage.message = 'You can now adjust event content if needed before publishing.';
			await eventStore.duplicateEvent(Number(eventId.value), payload);
			break;
		case EVENT_RECURRING_TYPE.DAILY:
		case EVENT_RECURRING_TYPE.WEEKLY:
		case EVENT_RECURRING_TYPE.BI_WEEKLY:
		case EVENT_RECURRING_TYPE.MONTHLY:
		case EVENT_RECURRING_TYPE.ANNUALLY:
			notificationMessage.title = 'Successfully drafted event series';
			notificationMessage.message = 'You can now adjust event content if needed before publishing.';

			await eventStore.duplicateEventSeries(Number(eventId.value), {
				...payload,
				eventCount: Number(data.eventCount),
				frequency:
					data.eventFrequency === EVENT_RECURRING_TYPE.MONTHLY
						? data.eventMonthlyOption
						: data.eventFrequency,
			});

			// Start polling event series
			await retrieveEventSeriesDetail({
				eventId: Number(eventId.value),
				eventSeriesId: eventStore.eventSeriesId,
			});
			break;
		default:
			break;
	}

	isCreatingEventSeries.value = false;
	showDialog.confirmDuplicate = false;
	if (!eventStore.duplicate.errorMessage) {
		if (data.eventFrequency === EVENT_RECURRING_TYPE.ONCE) {
			notificationStore.showSuccessNotification(
				notificationMessage.title,
				notificationMessage.message,
			);
		} else if (!eventStore.duplicate.eventSeriesResult?.failed.length) {
			notificationStore.showSuccessNotification(
				notificationMessage.title,
				notificationMessage.message,
				false,
				{ name: ROUTE.PROFILE_HOSTING.NAME },
				'See all events',
			);
		} else {
			// TODO: Show failed event items
		}

		trackEvent({
			create_event_method: GTM_DRAFT_EVENT_CREATED_PARAMS.DUPLICATED,
			event_id: eventStore.duplicate.eventId,
			event: GTM_EVENT_NAME.DRAFT_EVENT_CREATED,
		});
	}
}

async function handleDeleteEvent() {
	showDialog.confirmDelete = false;
	await eventStore.deleteEvent(Number(eventId.value));
	if (!eventStore.delete.errorMessage) {
		router.push({ name: ROUTE.PROFILE_HOSTING.NAME });
		notificationStore.showSuccessNotification('Successfully deleted the event.');
	}
}

async function handleTemporarilyCancelEvent() {
	showDialog.confirmCancel = true;
}

async function handleConfirmCancelEvent() {
	isCancellingEvent.value = true;
	const id = Number(eventId.value);

	await eventStore.temporarilyCancelEvent(id);
	if (!eventCancellationId.value) {
		return;
	}
	await eventStore.confirmCancelEvent(id, eventCancellationId.value);
	await eventStore.retrieveEventDetail(id);
	isCancellingEvent.value = false;
	showDialog.confirmCancel = false;
}

async function handleDiscardCancelEvent() {
	showDialog.confirmCancel = false;
}

function setEventDocumentTitle(isTrackGAEvent: boolean = true) {
	if (route.name?.toString().startsWith('event') && route.name !== ROUTE.EVENT_CREATE.NAME) {
		setDocumentTitle(route.name, eventStore.eventDetail?.title, isTrackGAEvent);
	}
}

function handleClosePasswordProtected() {
	// Redirect to unauthenticated view page
	router.push({
		name: ROUTE.ERROR_404.NAME,
	});
}

function handleConfirmPasswordProtected(password: string) {
	// Set password in session storage
	setSessionStorage(SESSION_STORAGE_ITEMS.EVENT_PASSWORD, password);

	// Force reload page to retrieve event detail with password
	const url = new URL(window.location.href);
	window.location.href = url.toString();
	isCheckingPassword.value = true;
}

onMounted(async () => {
	if (showTabs && eventId.value) {
		const accessCode = getSessionStorage(SESSION_STORAGE_ITEMS.EVENT_PASSWORD);
		await eventStore.retrieveEventDetail(Number(eventId.value), accessCode);
	}

	// Set document title on first load
	setEventDocumentTitle();

	if (eventStore.eventStatus && eventStore.eventStatus === EVENT_STATUS.DELETED) {
		// Redirect to first page if event deleted
		router.push({
			name: ROUTE.INDEX.NAME,
		});
	}
});

// Watch for changes in the route and update the activeTab accordingly
watch(
	() => route.name,
	(newRouteName) => {
		const matchedTab = tabs.value.find((tab) => tab.route.name === newRouteName);
		if (matchedTab) {
			activeTab.value = matchedTab;
		} else {
			activeTab.value = tabs.value[0]; // Fallback to the first tab if no match
		}
	},
);

watch(
	() => eventId.value,
	(newEventId) => {
		if (showTabs && newEventId) {
			const accessCode = getSessionStorage(SESSION_STORAGE_ITEMS.EVENT_PASSWORD);
			eventStore.retrieveEventDetail(Number(newEventId), accessCode);
		}
	},
);

watch(
	() => eventStore.duplicate.eventId,
	(newEventId) => {
		if (newEventId) {
			router.push({
				name: ROUTE.EVENT_EDIT.NAME,
				params: {
					id: newEventId,
				},
			});
		}
	},
);

watch(
	() => eventStore.cancel.errorMessage,
	(errorMessage) => {
		if (errorMessage) {
			notificationStore.showErrorNotification(errorMessage);
		}
	},
);

watch(
	() => eventStore.duplicate.errorMessage,
	(errorMessage) => {
		if (errorMessage) {
			notificationStore.showErrorNotification(errorMessage);
		}
	},
);

watch(route, () => {
	// Set document title when event page changed
	setEventDocumentTitle();
});

watch(
	() => eventStore.eventDetail?.organizerGroupId,
	async (groupId) => {
		if (groupId) {
			const promises = [groupStore.getGroupInfo(groupId)];
			if (profileStore.profileId) {
				promises.push(userStore.getGroupRole(profileStore.profileId, groupId));
			}
			await Promise.all(promises);
		}
	},
);

watch(
	() => userStore.userRole,
	async (role) => {
		if (role) {
			const currentUserId = profileStore.profileId;
			const eventOwnerID = eventStore.eventDetail?.organizerId;

			if (currentUserId !== eventOwnerID && route.name !== ROUTE.EVENT_VIEW.NAME && eventId.value) {
				// Allow admin role to edit event
				if (role === GROUP_ROLE.ADMIN) {
					return;
				}
				// Redirect to view page if current user is not event owner
				router.push({
					name: ROUTE.EVENT_VIEW.NAME,
					params: {
						id: eventId.value,
					},
				});
			}
		}
	},
);

watch(
	() => eventStore.eventDetail?.title,
	(newTitle) => {
		if (newTitle) {
			// Update browser title without GA tracking
			setEventDocumentTitle(false);
		}
	},
);
</script>

<template>
	<div
		:class="[
			'event-page-container page',
			{
				'-inform-page': isInformPage,
			},
		]"
	>
		<div v-if="showTabs" class="event-header">
			<div class="event-header-title">
				<div class="event-header-title-upper">
					<div class="title-section">
						<template v-if="isLoading">
							<TextInputSkeletonLoader
								class="title-loader"
								width="40%"
								height="24"
								:loading="isLoading"
							/>
						</template>
						<template v-else>
							<h5 class="text-md text-regular text-wrap">
								{{ groupName || '' }}
							</h5>
						</template>
					</div>

					<div class="action-section">
						<template v-if="isLoading">
							<ButtonSkeletonLoader width="100" height="38" />
							<ButtonSkeletonLoader width="38" height="38" />
							<template v-if="!isMobile">
								<ButtonSkeletonLoader width="38" height="38" />
								<ButtonSkeletonLoader width="38" height="38" />
							</template>
						</template>
						<template v-else>
							<EventStatusTag :status="eventStore.eventStatus" />
							<template v-if="isMobile">
								<BaseButton
									id="event-menu-activator"
									variant="outlined"
									color="gray"
									size="sm"
									icon-only
								>
									<DotsVerticalIcon />
								</BaseButton>
								<VMenu activator="#event-menu-activator" location="bottom" width="180">
									<VList class="event-menu-list text-sm text-medium">
										<VListItem
											v-if="isPublished"
											min-height="42"
											class="event-menu-item"
											:to="{
												name: ROUTE.EVENT_VIEW.NAME,
												params: {
													id: eventId,
												},
											}"
											target="_blank"
										>
											<template #prepend>
												<EyeIcon width="16" height="16" />
											</template>
											<span>View</span>
										</VListItem>
										<VListItem
											min-height="42"
											class="event-menu-item"
											@click="showDialog.confirmDuplicate = true"
										>
											<template #prepend>
												<CalendarPlus2Icon width="16" height="16" />
											</template>
											<span>Event series</span>
										</VListItem>
										<VListItem
											v-if="isDraft"
											min-height="42"
											class="event-menu-item"
											@click="showDialog.confirmDelete = true"
										>
											<template #prepend>
												<TrashIcon width="16" height="16" />
											</template>
											<span>Delete</span>
										</VListItem>
										<VListItem
											v-if="isPublished"
											min-height="42"
											class="event-menu-item"
											@click="handleTemporarilyCancelEvent"
										>
											<template #prepend>
												<CloseCircleIcon width="16" height="16" />
											</template>
											<span>Cancel</span>
										</VListItem>
									</VList>
								</VMenu>
							</template>
							<template v-else>
								<RouterLink
									v-if="isPublished"
									:to="{
										name: ROUTE.EVENT_VIEW.NAME,
										params: {
											id: eventId,
										},
									}"
									target="_blank"
								>
									<BaseButton ref="viewBtnRef" variant="outlined" color="gray" size="sm" icon-only>
										<EyeIcon />
									</BaseButton>
								</RouterLink>
								<VTooltip
									:activator="viewBtnRef"
									max-width="240"
									offset="8 0"
									location="bottom left"
								>
									<p class="tooltip">View event</p>
								</VTooltip>
								<BaseButton
									v-if="hasEventData"
									ref="duplicateBtnRef"
									variant="outlined"
									color="gray"
									size="sm"
									icon-only
									@click="showDialog.confirmDuplicate = true"
								>
									<CalendarPlus2Icon />
								</BaseButton>
								<VTooltip
									:activator="duplicateBtnRef"
									max-width="240"
									offset="8 0"
									location="bottom left"
								>
									<p class="tooltip">Draft an event series</p>
								</VTooltip>
								<BaseButton
									v-if="isDraft"
									ref="deleteBtnRef"
									variant="outlined"
									color="gray"
									size="sm"
									icon-only
									@click="showDialog.confirmDelete = true"
								>
									<TrashIcon />
								</BaseButton>
								<VTooltip
									:activator="deleteBtnRef"
									max-width="240"
									offset="8 0"
									location="bottom left"
								>
									<p class="tooltip">Delete event</p>
								</VTooltip>
								<BaseButton
									v-if="isPublished"
									ref="cancelBtnRef"
									:variant="BtnVariant[1]"
									:color="BtnColor[1]"
									:size="BtnSize[0]"
									icon-only
									@click="handleTemporarilyCancelEvent"
								>
									<CloseCircleIcon />
								</BaseButton>
								<VTooltip
									:activator="cancelBtnRef"
									max-width="240"
									offset="8 0"
									location="bottom left"
								>
									<span class="tooltip"> Cancel event </span>
								</VTooltip>
							</template>
						</template>
					</div>
				</div>
				<div class="event-header-title-lower">
					<template v-if="isLoading">
						<TextInputSkeletonLoader
							class="title-loader"
							width="50%"
							height="38"
							:loading="isLoading"
						/>
					</template>
					<template v-else>
						<h2 class="text-display-sm text-semibold">
							{{ eventStore.edit.title }}
						</h2>
					</template>
				</div>
			</div>
			<VTabs v-if="!isLoading" v-model="activeTab" class="tabs" centered height="32px">
				<VTab
					v-for="tab in tabs"
					:key="tab.name"
					:to="tab.route"
					:class="['event-menu-tab', { 'v-tab--selected': tab.route.name === route.name }]"
					:ripple="false"
					exact
				>
					{{ tab.name }}
				</VTab>
			</VTabs>
		</div>
		<BaseAlert v-if="alertEventMessage" class="event-alert" :type="ALERT_TYPE.INFO">
			<span class="text-sm text-semibold">
				{{ alertEventMessage }}
			</span>
		</BaseAlert>
		<BaseAlert v-if="alertCountdownReminderMessage" class="event-alert" :type="ALERT_TYPE.WARNING">
			<span class="text-sm text-semibold">
				{{ alertCountdownReminderMessage }}
			</span>
		</BaseAlert>
		<RouterView />
		<DuplicateEventDialog
			:is-show="showDialog.confirmDuplicate"
			:is-loading="isCreatingEventSeries"
			:timezone="eventStore.eventDetail?.timezone"
			@on-close="showDialog.confirmDuplicate = false"
			@on-confirm="handleDuplicateEvent"
		/>
		<ConfirmDialog
			:is-show="showDialog.confirmDelete"
			confirm-button-name="Delete"
			confirm-button-color="error"
			@on-close="showDialog.confirmDelete = false"
			@on-confirm="handleDeleteEvent"
		>
			<template #header>
				<div class="icon-header-confirm-dlg icon-error">
					<TrashIcon width="24" height="24" :color="ICON_COLOR.ERROR" />
				</div>
			</template>
			<template #title> Delete event </template>
			<template #content>
				<p class="text-sm text-regular">
					Are you sure you want to delete this event? This cannot be undone.
				</p>
			</template>
		</ConfirmDialog>
		<ConfirmDialog
			:is-show="showDialog.confirmCancel"
			confirm-button-name="Proceed"
			confirm-button-color="error"
			cancel-button-name="Go back"
			persistent
			:is-loading="isCancellingEvent"
			@on-close="handleDiscardCancelEvent"
			@on-confirm="handleConfirmCancelEvent"
		>
			<template #header>
				<div class="icon-header-confirm-dlg icon-error">
					<CloseCircleIcon width="24" height="24" :color="ICON_COLOR.ERROR" />
				</div>
			</template>
			<template #title> Cancel event </template>
			<template #content>
				<div class="cancel-dlg-content">
					<p class="text-sm text-regular">
						Full refunds will be issued to all paid ticket buyers. You will still be responsible for
						all incurred platform and processing fees.
					</p>
					<p class="text-sm text-regular">Are you sure you want to proceed?</p>
				</div>
			</template>
		</ConfirmDialog>
		<PasswordProtectedDialog
			:is-show="eventStore.protect.isPasswordProtected"
			:is-loading="isCheckingPassword"
			@on-close="handleClosePasswordProtected"
			@on-confirm="handleConfirmPasswordProtected"
		/>
	</div>
</template>
<style scoped lang="scss">
.event-header {
	margin-bottom: spacings-get(8);

	@include media-query-max(mobile) {
		padding: 0 spacings-get(4);
	}

	.event-header-title {
		display: flex;
		flex-direction: column;
		align-items: start;
		gap: spacings-get(2);
		margin-bottom: spacings-get(3);

		.event-header-title-upper {
			width: 100%;
			display: flex;
			justify-content: space-between;
			align-items: center;
			gap: spacings-get(3);
		}

		.event-header-title-lower {
			width: 100%;
		}

		.title-section {
			width: 100%;
			display: flex;
			flex-direction: column;
			gap: spacings-get(2);

			.title-loader {
				:deep(.v-skeleton-loader__text) {
					height: rem(32);

					&:first-child {
						height: rem(38);
					}
				}
			}
		}

		.action-section {
			display: flex;
			gap: spacings-get(3);
		}
	}

	.event-menu-tab {
		min-width: auto;
	}
}

.event-menu-item {
	gap: spacings-get(2);
	box-sizing: border-box;
}

.event-alert {
	margin-bottom: spacings-get(8);

	@include media-query-max(mobile) {
		margin: 0 spacings-get(4) spacings-get(8);
	}
}

.cancel-dlg-content {
	display: flex;
	flex-direction: column;
	gap: spacings-get(4);

	p {
		display: flex;
		flex-direction: column;
		gap: spacings-get(1);
	}
}

.event-menu-list {
	:deep(.v-list-item) {
		padding: 0 spacings-get(4) !important;
	}
}

.event-page-container.-inform-page {
	min-height: inherit;
}
</style>
